// ============================================================
// ランタイム設定 / 定数
// ============================================================

import type { LatLon } from './types'

/** 東京駅丸の内中央口前 */
export const TOKYO_STATION: LatLon = { lat: 35.6812, lon: 139.7671 }

/** つくば駅 (TX終点) */
export const TSUKUBA_STATION: LatLon = { lat: 36.0820, lon: 140.1112 }

/**
 * Mock のデフォルト位置。東京駅にしておくと駅・バス・路線が全部見える。
 * 実機 GPS テストは別途実機で。
 */
export const DEFAULT_MOCK_LOCATION: LatLon = TOKYO_STATION

/** 開発用の代替モック地点 — localStorage.devMock で切替 */
export const MOCK_LOCATIONS: Record<string, LatLon> = {
  tsukuba:    TSUKUBA_STATION,                  // つくば駅 (デフォルト)
  moriya:     { lat: 35.9492, lon: 139.9756 },  // 守谷駅
  akihabara:  { lat: 35.6987, lon: 139.7731 },  // 秋葉原駅 (TX 始発)
  tokyo:      TOKYO_STATION,
  shinjuku:   { lat: 35.6896, lon: 139.7006 },
  shibuya:    { lat: 35.6580, lon: 139.7016 },
  ikebukuro:  { lat: 35.7295, lon: 139.7109 },
}

/** ソナー半径のサイクル候補 (m) — 都心〜郊外〜広域対応 */
export const RADIUS_STEPS = [500, 1000, 5000, 10000] as const

/** ソナー初期半径 — 都心で 1km なら適度な密度 */
export const DEFAULT_RADIUS_M: number = 1000

/** 地図 view は sonar 半径の何倍を表示するか */
export const MAP_RADIUS_MULTIPLIER = 2

/** GTFS-RT VehiclePosition のポーリング間隔 (ms) */
export const POLL_VEHICLE_MS = 5_000

/** odpt:Train のポーリング間隔 (ms) */
export const POLL_TRAIN_MS = 15_000

/** ソナー描画のレートリミット (ms) — image container update */
export const SONAR_RENDER_INTERVAL_MS = 1_000

/** スポットライト text 更新のレートリミット (ms) */
export const SPOTLIGHT_UPDATE_MS = 1_000

/**
 * ODPT へのアクセス先 origin。
 * - dev: Vite が proxy するので空文字列 (相対 URL)
 * - prd: CloudFront distribution
 */
export const ODPT_ORIGIN = (() => {
  if (import.meta.env.DEV) return ''
  // 本番ビルド時に環境変数で差し替え可。デフォルトは dev distribution。
  return import.meta.env.VITE_CDN_ORIGIN ?? 'https://oec-tokyo-scouter.dev.tailstone.net'
})()

/** 公開 API (キー不要) のパスプレフィックス */
export const ODPT_PUBLIC_PREFIX = '/odpt-public'

/** 認証 API (キーは edge で注入) のパスプレフィックス */
export const ODPT_PREFIX = '/odpt'

/**
 * PLATEAU 建物 MVT のパスプレフィックス。
 * - dev: Vite proxy が indigo-lab.github.io にリライト
 * - prd: CloudFront が同じ behavior を持つ (将来 enable)
 */
export const PLATEAU_BUILDINGS_PREFIX = '/plateau-buildings'

/**
 * OSRM (フリーのルーティングサーバー) のパスプレフィックス。
 * - dev: Vite proxy が router.project-osrm.org にリライト
 * - prd: CloudFront /osrm/* behavior でプロキシ
 */
export const OSRM_PREFIX = '/osrm'

/**
 * OpenStreetMap タイルのパスプレフィックス。
 * - dev: Vite proxy が tile.openstreetmap.org にリライト
 * - prd: CloudFront /osm-tiles/* behavior でプロキシ
 */
export const OSM_TILES_PREFIX = '/osm-tiles'

/** 主要事業者 ID (odpt:Operator) */
export const OPERATORS = {
  toei: 'odpt.Operator:Toei',
  tokyoMetro: 'odpt.Operator:TokyoMetro',
  jrEast: 'odpt.Operator:JR-East',
  // 東京臨海高速鉄道 (りんかい線、新木場〜大崎)
  twr: 'odpt.Operator:TWR',
  // つくばエクスプレス (首都圏新都市鉄道)。秋葉原〜つくば 58.3km
  tx: 'odpt.Operator:MIR',
  // 関東鉄道 (常総線/竜ヶ崎線、つくば〜土浦〜守谷)
  kantoRailway: 'odpt.Operator:KantoRailway',
  // 関東バス (都内バス、GTFS-RT 提供)
  kantoBus: 'odpt.Operator:KantoBus',
} as const

/** 鉄道 operator の集合 (静的駅 + 運行情報 fetch 対象) */
export const RAIL_OPERATORS = [
  OPERATORS.toei,
  OPERATORS.tokyoMetro,
  OPERATORS.jrEast,
  OPERATORS.twr,
  OPERATORS.tx,
  OPERATORS.kantoRailway,
] as const

/**
 * 都営バスの GTFS Static zip URL (公式)。
 * routes.txt / trips.txt が読まれ、バス名が "都02 渋谷駅前行" 形式になる。
 * VITE_GTFS_TOEI_BUS_URL で上書き可能。
 */
export const GTFS_STATIC_URL_TOEI_BUS: string =
  import.meta.env.VITE_GTFS_TOEI_BUS_URL ??
  `${ODPT_ORIGIN}${ODPT_PUBLIC_PREFIX}/api/v4/files/Toei/data/ToeiBus-GTFS.zip`

/**
 * 都営地下鉄/さくらトラムの GTFS Static zip URL (公式)。
 * 路線・駅・時刻・shape を含む。
 */
export const GTFS_STATIC_URL_TOEI_TRAIN: string =
  import.meta.env.VITE_GTFS_TOEI_TRAIN_URL ??
  `${ODPT_ORIGIN}${ODPT_PUBLIC_PREFIX}/api/v4/files/Toei/data/Toei-Train-GTFS.zip`

/**
 * 関東バス GTFS Static。
 * **`?date=YYYYMMDD` が必須** (省略すると ODPT が 404 を返す)。
 * date は ODPT に登録されている版の "有効期間開始日"。
 * 最新版は https://ckan.odpt.org/dataset/kanto_bus_all_lines で確認できる。
 * 新版が出たら下記 KANTO_BUS_GTFS_DATE を更新する (古い版も当面は配信される)。
 */
const KANTO_BUS_GTFS_DATE = '20260507'
export const GTFS_STATIC_URL_KANTO_BUS: string =
  import.meta.env.VITE_GTFS_KANTO_BUS_URL ??
  `${ODPT_ORIGIN}${ODPT_PREFIX}/api/v4/files/odpt/KantoBus/AllLines.zip?date=${KANTO_BUS_GTFS_DATE}`

/** 東京メトロ GTFS Static */
export const GTFS_STATIC_URL_TOKYO_METRO: string =
  import.meta.env.VITE_GTFS_TOKYO_METRO_URL ??
  `${ODPT_ORIGIN}${ODPT_PREFIX}/api/v4/files/TokyoMetro/data/TokyoMetro-Train-GTFS.zip`

/** TX (MIR) GTFS Static */
export const GTFS_STATIC_URL_MIR: string =
  import.meta.env.VITE_GTFS_MIR_URL ??
  `${ODPT_ORIGIN}${ODPT_PREFIX}/api/v4/files/MIR/data/MIR-Train-GTFS.zip`

/** TWR (りんかい線) GTFS Static */
export const GTFS_STATIC_URL_TWR: string =
  import.meta.env.VITE_GTFS_TWR_URL ??
  `${ODPT_ORIGIN}${ODPT_PREFIX}/api/v4/files/TWR/data/TWR-Train-GTFS.zip`

/** SDK が想定する G2 画面寸法 */
export const G2_CANVAS = { width: 576, height: 288 } as const

/** 画像コンテナ上限 */
export const G2_IMAGE_MAX = { width: 200, height: 100 } as const

/** ソナー image のレイアウト座標 */
export const SONAR_IMAGE_RECT = { x: 376, y: 94, width: 200, height: 100 } as const

/** スポットライトの選定アルゴ用パラメータ */
export const AGGREGATOR = {
  /** 自分の進行方向 ±この角度 (度) のスコアにボーナス */
  headingBonusHalfWidth: 60,
  /** ボーナス倍率 */
  headingBonusFactor: 1.5,
  /** ETA 不明のときの仮置き (分) */
  defaultEtaMins: 99,
} as const
